import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import useOrderEndpoints from '@/composables/api/useOrderEndpoints';

const AMOUNT_KEYS = ['fiatAmount', 'coinAmount'];
const FEE_KEYS = ['networkFee', 'gatewayFee', 'quoteValue', 'receivedRate', 'spread'];

const isPresent = (value) => value !== undefined && value !== null;

/**
 * The single writer for order pricing outside the pricing calculator. Keeps the order
 * accordion (localStorage) and the active order store's form + feeData (sessionStorage)
 * in step. Missing keys are skipped; a zero is a real value and is written.
 */
export const applyOrderPricing = (values = {}) => {
  const orderStore = useOrderFormStore();
  const activeStore = orderStore.getActiveStore;

  AMOUNT_KEYS.filter((key) => isPresent(values[key])).forEach((key) => {
    orderStore.updateOrderAccordion(key, values[key]);
    activeStore?.updateValue(key, values[key], true);
  });

  FEE_KEYS.filter((key) => isPresent(values[key])).forEach((key) => {
    orderStore.updateOrderAccordion(key, values[key]);
    activeStore?.updateFeeData(key, values[key]);
  });
};

/** Maps Core's switch-psp `order` payload onto pricing keys. */
export const mapSwitchedOrderToPricing = (order = {}) => {
  const pricing = {
    fiatAmount: order.currencyValue,
    coinAmount: order.coinValue,
    gatewayFee: order.paymentFee ?? order.fee,
    networkFee: order.networkFee,
  };

  return Object.fromEntries(
    Object.entries(pricing).filter(([, value]) => isPresent(value)),
  );
};

/**
 * Re-reads pricing from spa-be's cart summary, which is built from the session quote.
 * After a PSP switch that quote is the one priced against the new payment method, so it
 * carries the quote value Core's switch response does not. Failures leave values as they are.
 */
export const refreshOrderPricingFromSummary = async () => {
  const { getCartSummary } = useOrderEndpoints();

  try {
    const response = await getCartSummary();
    const summary = response?.data?.data?.data ?? response?.data?.data;
    if (summary) {
      applyOrderPricing(summary);
    }
  } catch {
    // Non-blocking: the values from the switch response are already applied.
  }
};

export default function useOrderPricingSync() {
  return {
    applyOrderPricing,
    mapSwitchedOrderToPricing,
    refreshOrderPricingFromSummary,
  };
}
